import InputField from "@shared/components/Atoms/InputField/InputField.component";
import { UserAddress } from "@shared/types/UserAccountTypes.interface";
import { useTranslations } from "@shared/context/useTranslations";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { cn } from "@shared/helpers/classNames.helper";
import { CountryPicker } from "../CountryPicker/CountryPicker.component";
import { SelectOption } from "@shared/helpers/countryHelper";
import S from "./AddressEdit.module.scss";

export interface AddressEditProps {
    address: UserAddress,
    allowSaveAddressOverride?: boolean,
}

export type AddressEditRef = {
	validate: () => UserAddressState;
    reset: (defaultCountryId: string) => void;
};

export interface UserAddressState {
    isValid: boolean;
    address: UserAddress | null;
    saveAddress: boolean
}

// eslint-disable-next-line react/display-name
export const AddressEdit = forwardRef<AddressEditRef, AddressEditProps>(
    ({address, allowSaveAddressOverride}: AddressEditProps, ref) => {
        const { getTranslation } = useTranslations();   
        const [hidePostalCode, setHidePostalCode] = useState<boolean>(false);
        const [showState, setShowState] = useState<boolean>(false);
        const [saveAddress, setSaveAddress] = useState<boolean>(true);
        const [errors, setErrors] = useState<{ [key: string]: string }>({});
        const [countryCode, setCountryCode] = useState<string>(address?.countryId || "");
        const [id] = useState<string | undefined>(address?.id || undefined);
        const [alias, setAlias] = useState<string>(address?.alias /* || "" */);
        const [companyName, setCompanyName] = useState<string>(address?.companyName /* || "" */);
        const [streetName, setStreetName] = useState<string>(address?.streetName || "");
        const [postalCode, setPostalCode] = useState<string>(address?.postalCode || "");
        const [cityName, setCityName] = useState<string>(address?.cityName || "");
        const [stateName, setStateName] = useState<string>(address?.stateName || "");
        const [countyName, setCountyName] = useState<string>(address?.countyName || "");

        useEffect(() => {
            if (address?.countryId) {
                setCountryCode(address.countryId);
            }
        }, [address]);

        const handleSaveAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setSaveAddress(e.target.checked);
        };
    
        const validateAddress = () => {
            const validationErrors: { [key: string]: string } = {};
    
            if (saveAddress && allowSaveAddressOverride && !alias) {
                validationErrors.alias = getTranslation("addressEdit.errorAlias");
            }
            if (!alias) {
                validationErrors.alias = getTranslation("addressEdit.errorAlias");
            }
            if (!companyName) {
                validationErrors.companyName = getTranslation("addressEdit.errorCompanyName");
            }
            if (!streetName) {
                validationErrors.streetName = getTranslation("addressEdit.errorStreetName");
            }
            if (!hidePostalCode && !postalCode) {
                validationErrors.postalCode = getTranslation("addressEdit.errorPostalCode");
            }
            if (!cityName) {
                validationErrors.cityName = getTranslation("addressEdit.errorCityName");
            }
            if (showState && !stateName) {
                validationErrors.stateName = getTranslation("addressEdit.errorStateName");
            }
    
            setErrors(validationErrors);

            return Object.keys(validationErrors).length === 0
        }

        useEffect(() => {
            switch (countryCode) {
                case "HKG":
                case "ARE":
                    setHidePostalCode(true);
                    setShowState(false);                
                    break;
    
                case "AUS":
                case "USA":
                case "CAN":
                    setHidePostalCode(false);
                    setShowState(true);
                    break;
            
                default:
                    setHidePostalCode(false);
                    setShowState(false);
            }
        }, [countryCode]);

        // public methods
        useImperativeHandle(ref, () => ({            
            validate: () => {

                const address = validateAddress() ? {
                    id,
                    alias,
                    companyName,
                    streetName,
                    postalCode,
                    cityName,
                    stateName,
                    countryId: countryCode,
                    countyName
                } : null;

                return {
                    isValid: address !== null,
                    address,
                    saveAddress
                }
            },
            reset: (defaultCountryId: string) => {
                setAlias("");
                setCompanyName("");
                setStreetName("");
                setPostalCode("");
                setCityName("");
                setStateName("");
                setCountyName("");
                setCountryCode(defaultCountryId);
                setErrors({});
            }
        }));

        return (
            <div className={cn(
                S.inputFields,
                hidePostalCode && S.hidePostalCode,
                showState && S.showState
            )}>
                <div className={S.span3}>
                    <InputField
                        className={cn(S.inputField, S.inputBold, S.span3, errors.alias && S.inputError)}
                        type="text"
                        name="alias"
                        disabled={allowSaveAddressOverride && !saveAddress}
                        value={alias}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlias(e.target.value)}
                        label={getTranslation("AddressEdit.alias")}
                        error={errors.alias}
                    />
                    {allowSaveAddressOverride ? (
                        <div className={cn(S.inputFieldHelpText, errors.alias && S.inputError)}>
                            {getTranslation("AddressEdit.aliasMessage")}
                        </div>
                    ) : null}
                </div>

                <div className={S.span3}>
                    <InputField
                        className={cn(S.inputField, S.span3, errors.alias && S.inputError)}
                        type="text"
                        name="companyName"
                        value={companyName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
                        label={getTranslation("AddressEdit.companyName")}
                        error={errors.companyName}
                    />
                </div>

                <div className={S.countryPicker}>
                    <label>{getTranslation("AddressEdit.country")}</label>
                    <CountryPicker
                        selectedCountryCode={countryCode}
                        onUpdate={(option: SelectOption) => setCountryCode(option.value)}
                        maxVisibleOptions={3}
                    />
                </div>

                <InputField
                    className={cn(
                        S.inputField,
                        S.span2, 
                        errors.streetName && S.inputError
                    )}
                    type="text"
                    name="streetName"
                    value={streetName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStreetName(e.target.value)}
                    label={getTranslation("AddressEdit.streetName")}
                    error={errors.streetName}
                />


                {!hidePostalCode ? (
                    <InputField
                        className={cn(S.inputField, errors.postalCode && S.inputError)}
                        value={postalCode}
                        name="postalCode"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostalCode(e.target.value)}
                        label={getTranslation("AddressEdit.postalCode")}
                        error={errors.postalCode}
                    />
                ) : null}

                <InputField
                    className={cn(
                        S.inputField,
                        !showState && S.span2,
                        hidePostalCode && S.span3,
                        errors.cityName && S.inputError
                    )}
                    name="cityName"
                    value={cityName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCityName(e.target.value)}
                    label={getTranslation("AddressEdit.cityName")}
                    error={errors.cityName}
                />

                {showState ? (
                    <InputField
                        className={cn(S.inputField, errors.stateName && S.inputError)}
                        name="stateName"
                        value={stateName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStateName(e.target.value)}
                        label={getTranslation("AddressEdit.stateName")}
                        error={errors.stateName}
                    />
                ) : null}

                {allowSaveAddressOverride ? (
                    <div className={S.span3}>
                        <label className={S.checkboxLabel}>
                            <input
                                className={S.checkbox}
                                type="checkbox"
                                checked={saveAddress}
                                aria-checked={saveAddress}
                                onChange={handleSaveAddressChange}
                            />
                            <span>{getTranslation("AddressEdit.saveAddressToList")}</span>
                        </label>
                    </div>
                ) : null}


                {Object.keys(errors).length > 0 ? (
                <div className={cn(S.errorMessage, S.span3)}>
                    <span className={S.fixErrors}>
                        {getTranslation("AddressEdit.fixErrors")}
                    </span>
                    <ul>
                        {Object.values(errors).map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                </div>
                ) : null}
            </div>
        )
    }
);
